import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useAuth } from 'react-oidc-context';

import log from '@src/datadog';

interface AuthenticationContextProps {
  doLogout?: () => void;
}

const AuthenticationContext = createContext<AuthenticationContextProps>({});

const AuthenticationContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const auth = useAuth();

  const doLogout = useCallback(async () => {
    await auth.removeUser();
    await auth.revokeTokens(['access_token', 'refresh_token']);
    await auth.clearStaleState();

    if (auth.user?.id_token) {
      await auth.signoutRedirect({
        post_logout_redirect_uri: window.location.origin,
        id_token_hint: auth.user.id_token,
      });
    } else {
      await auth.signinRedirect();
    }

    log.clearDDUser();
  }, [auth]);

  const authenticationContextValue = useMemo(
    () => ({
      doLogout,
    }),
    [doLogout],
  );

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {!auth.isLoading && children}
    </AuthenticationContext.Provider>
  );
};

const useAuthenticationContext = () => {
  return useContext(AuthenticationContext);
};

export {
  AuthenticationContext,
  AuthenticationContextProvider,
  useAuthenticationContext,
};
